import { useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { CaptureScreen } from "@/components/capture/CaptureScreen"
import { VoiceRecordingScreen } from "@/components/capture/VoiceRecordingScreen"
import { AudioUploadScreen } from "@/components/capture/AudioUploadScreen"
import { FileUploadScreen } from "@/components/capture/FileUploadScreen"
import { SaveFlowScreen } from "@/components/capture/SaveFlowScreen"

type CaptureMode = "voice" | "audio" | "editor" | "file" | null
type FlowStep = "select" | "capture" | "save"

interface FrontMatterField {
  id: number
  name: string
  options: Array<{ id: number; label: string }>
}

interface RoutingRule {
  front_matter_field_id: number
  front_matter_option_id: number
  destination_folder: string
}

interface HomeProps {
  dropboxConnected: boolean
  frontMatterFields: FrontMatterField[]
  textNotesDestination: string
  voiceNotesDestination: string
  uploadsDestination: string
  rootFolder: string
  fileNameTemplate: string
  routingRules: RoutingRule[]
}

export default function Home() {
  const {
    dropboxConnected,
    frontMatterFields,
    textNotesDestination,
    voiceNotesDestination,
    uploadsDestination,
    rootFolder,
    fileNameTemplate,
    routingRules,
  } = usePage().props as unknown as HomeProps

  const [mode, setMode] = useState<CaptureMode>(null)
  const [step, setStep] = useState<FlowStep>("select")
  const [recordingData, setRecordingData] = useState<{
    key: string | null
    uploadId: string | null
    parts: Array<{ etag: string; part_number: number }>
    elapsed?: number
    fileName?: string
  } | undefined>(undefined)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  function handleSelectMode(selected: CaptureMode) {
    if (selected === "editor") {
      router.visit("/new")
      return
    }
    setMode(selected)
    setStep("capture")
  }

  function handleRecordingComplete(data: { key: string | null; uploadId: string | null; parts: Array<{ etag: string; part_number: number }>; elapsed: number }) {
    setRecordingData(data)
    setStep("save")
  }

  function handleAudioComplete(data: { fileName: string }) {
    setRecordingData({ key: null, uploadId: null, parts: [], fileName: data.fileName })
    setStep("save")
  }

  function handleFileSelected(data: { file: File }) {
    setSelectedFile(data.file)
    setStep("save")
  }

  function handleCancel() {
    setMode(null)
    setStep("select")
    setRecordingData(undefined)
    setSelectedFile(null)
  }

  function handleSaved() {
    setMode(null)
    setStep("select")
    setRecordingData(undefined)
    setSelectedFile(null)
  }

  function getDefaultDestination(): string {
    switch (mode) {
      case "voice":
      case "audio":
        return voiceNotesDestination
      case "file":
        return uploadsDestination
      case "editor":
        return textNotesDestination
      default:
        return textNotesDestination
    }
  }

  return (
    <>
      <Head title="Capture" />

      {step === "select" && (
        <CaptureScreen onSelectMode={handleSelectMode} dropboxConnected={dropboxConnected} />
      )}

      {step === "capture" && mode === "voice" && (
        <VoiceRecordingScreen
          onComplete={handleRecordingComplete}
          onCancel={handleCancel}
        />
      )}

      {step === "capture" && mode === "audio" && (
        <AudioUploadScreen
          onComplete={handleAudioComplete}
          onCancel={handleCancel}
        />
      )}

      {step === "capture" && mode === "file" && (
        <FileUploadScreen
          onComplete={handleFileSelected}
          onCancel={handleCancel}
        />
      )}

      {step === "save" && mode && (
        <SaveFlowScreen
          captureMode={mode as "voice" | "audio" | "editor" | "file"}
          recordingData={recordingData}
          frontMatterFields={frontMatterFields}
          defaultDestination={getDefaultDestination()}
          rootFolder={rootFolder}
          fileNameTemplate={fileNameTemplate}
          routingRules={routingRules}
          selectedFile={selectedFile}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      )}
    </>
  )
}
